import apiClient from "./api";
import { BaseEntity, ListResponse, SearchParams } from "../types";
import { buildSearchParams, buildSelectQuery } from "../utils/apiHelpers";
import {
  generateCacheKey,
  networkFirst,
  cacheFirst,
  staleWhileRevalidate,
} from "../storage/cacheManager";
import {
  requestDeduplication,
  generateRequestKey,
} from "../utils/requestDeduplication";

export type CacheStrategy =
  | "network-first"
  | "cache-first"
  | "stale-while-revalidate"
  | "no-cache";

export class BaseService<T extends BaseEntity> {
  protected cacheStrategy: CacheStrategy = "network-first";
  protected cacheExpiry: number = 5 * 60 * 1000;

  constructor(protected entityName: string) {}

  setCacheStrategy(strategy: CacheStrategy): void {
    this.cacheStrategy = strategy;
  }

  setCacheExpiry(expiry: number): void {
    this.cacheExpiry = expiry;
  }

  async getList(params?: SearchParams): Promise<ListResponse<T>> {
    const entityKeyMap: Record<string, string> = {
      Task: "tasks:list",
      Meeting: "meetings:list",
      Project: "projects:list",
      Opportunity: "opportunities:list",
    };
    const cacheKey =
      entityKeyMap[this.entityName] ||
      generateCacheKey(`/${this.entityName}/list`, params);
    const url = params
      ? `/${this.entityName}?searchParams=${buildSearchParams(params)}`
      : `/${this.entityName}`;
    const requestKey = generateRequestKey("GET", url, params);

    const fetchData = async (
      signal?: AbortSignal,
    ): Promise<ListResponse<T>> => {
      if (params) {
        const searchParams = buildSearchParams(params);
        const response = await apiClient.get<ListResponse<T>>(
          `/${this.entityName}?searchParams=${searchParams}`,
          { signal },
        );
        return response.data;
      }
      const response = await apiClient.get<ListResponse<T>>(
        `/${this.entityName}`,
        { signal },
      );
      return response.data;
    };

    const deduplicatedFetch = () =>
      requestDeduplication.execute(requestKey, fetchData);

    if (this.cacheStrategy === "no-cache") {
      return deduplicatedFetch();
    }

    if (this.cacheStrategy === "cache-first") {
      return cacheFirst(cacheKey, deduplicatedFetch, this.cacheExpiry);
    }

    if (this.cacheStrategy === "stale-while-revalidate") {
      return staleWhileRevalidate(
        cacheKey,
        deduplicatedFetch,
        this.cacheExpiry,
      );
    }

    return networkFirst(cacheKey, deduplicatedFetch, this.cacheExpiry);
  }

  async getById(id: string, selectFields?: string[]): Promise<T> {
    const cacheKey = selectFields?.length
      ? generateCacheKey(`/${this.entityName}/${id}`, { select: selectFields })
      : generateCacheKey(`/${this.entityName}/${id}`);
    const selectQuery = selectFields?.length
      ? `?select=${buildSelectQuery(selectFields)}`
      : "";
    const url = `/${this.entityName}/${id}${selectQuery}`;
    const requestKey = generateRequestKey("GET", url, { select: selectFields });

    const fetchData = async (signal?: AbortSignal): Promise<T> => {
      const response = await apiClient.get<T>(url, { signal });
      return response.data;
    };

    const deduplicatedFetch = () =>
      requestDeduplication.execute(requestKey, fetchData);

    if (this.cacheStrategy === "no-cache") {
      return deduplicatedFetch();
    }

    if (this.cacheStrategy === "cache-first") {
      return cacheFirst(cacheKey, deduplicatedFetch, this.cacheExpiry);
    }

    if (this.cacheStrategy === "stale-while-revalidate") {
      return staleWhileRevalidate(
        cacheKey,
        deduplicatedFetch,
        this.cacheExpiry,
      );
    }

    return networkFirst(cacheKey, deduplicatedFetch, this.cacheExpiry);
  }

  async create(data: Partial<T>): Promise<T> {
    const response = await apiClient.post<T>(`/${this.entityName}`, data);

    await this.invalidateListCache();

    return response.data;
  }

  async update(id: string, data: Partial<T>): Promise<T> {
    const response = await apiClient.put<T>(`/${this.entityName}/${id}`, data);

    await this.invalidateCache(id);
    await this.invalidateListCache();

    return response.data;
  }

  async delete(id: string): Promise<void> {
    await apiClient.delete(`/${this.entityName}/${id}`);

    await this.invalidateCache(id);
    await this.invalidateListCache();
  }

  async getRelated(
    id: string,
    relation: string,
    selectFields?: string[],
  ): Promise<ListResponse<T>> {
    const cacheKey = generateCacheKey(`/${this.entityName}/${id}/${relation}`, {
      select: selectFields,
    });
    const selectQuery = selectFields
      ? `?select=${buildSelectQuery(selectFields)}`
      : "";
    const url = `/${this.entityName}/${id}/${relation}${selectQuery}`;
    const requestKey = generateRequestKey("GET", url, { select: selectFields });

    const fetchData = async (
      signal?: AbortSignal,
    ): Promise<ListResponse<T>> => {
      const response = await apiClient.get<ListResponse<T>>(url, { signal });
      return response.data;
    };

    const deduplicatedFetch = () =>
      requestDeduplication.execute(requestKey, fetchData);

    if (this.cacheStrategy === "no-cache") {
      return deduplicatedFetch();
    }

    if (this.cacheStrategy === "cache-first") {
      return cacheFirst(cacheKey, deduplicatedFetch, this.cacheExpiry);
    }

    if (this.cacheStrategy === "stale-while-revalidate") {
      return staleWhileRevalidate(
        cacheKey,
        deduplicatedFetch,
        this.cacheExpiry,
      );
    }

    return networkFirst(cacheKey, deduplicatedFetch, this.cacheExpiry);
  }

  protected async invalidateCache(id: string): Promise<void> {
    const { removeFromCache, getCacheKeys } =
      await import("../storage/cacheManager");
    const keys = await getCacheKeys();
    const idPrefix = `/${this.entityName}/${id}`;
    const keysToRemove = keys.filter((key) => key.startsWith(idPrefix));
    await Promise.all(keysToRemove.map((key) => removeFromCache(key)));
  }

  protected async invalidateListCache(): Promise<void> {
    const { removeFromCache, getCacheKeys } =
      await import("../storage/cacheManager");
    const keys = await getCacheKeys();
    const listKeys = keys.filter((key) =>
      key.includes(`/${this.entityName}/list`),
    );
    await Promise.all(listKeys.map((key) => removeFromCache(key)));
  }
}
